import { makeValuesPredicate } from '@sgme/fp';
import { sortValidationErrors } from 'components/PostTrade/ProductModification/components/ValidationErrors.utils';
import type { FormError, FormRecord } from 'models/formError';
import { useMemo } from 'react';

export const shouldDisplayErrorInSummary = <
  Form = FormRecord,
  Keys extends string = string,
  KeysPrefix extends string = string,
>(
  hiddenFields: readonly (keyof Form)[],
) => {
  const isHiddenField = makeValuesPredicate(hiddenFields);
  /**
   * An error should be displayed in summary if its flag is true or it has no fields visible to display the error on
   *
   * (Array#every returns true when the array is empty)
   */
  return ({
    fields = [],
    isDisplayedInResume,
    isDisplayedInSummary = isDisplayedInResume,
  }: FormError<Form, Keys, KeysPrefix>): boolean => isDisplayedInSummary || fields.every(isHiddenField);
};

export function useFilterErrorForSidePanel<
  Form = FormRecord,
  AllKeys extends string = string,
  KeyPrefix extends string = string,
>(
  validationErrors: readonly FormError<Form, AllKeys, KeyPrefix>[],
  onlyErrorsDisplayedInSummary: boolean,
  hiddenFields: readonly (keyof Form)[],
): readonly FormError<Form, AllKeys, KeyPrefix>[] {
  return useMemo(() => {
    const shouldBeDisplayedInSummary = onlyErrorsDisplayedInSummary
      ? shouldDisplayErrorInSummary<Form, AllKeys, KeyPrefix>(hiddenFields)
      : () => true;
    return sortValidationErrors(validationErrors.filter(shouldBeDisplayedInSummary));
  }, [hiddenFields, onlyErrorsDisplayedInSummary, validationErrors]);
}
